import React from 'react';
import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';

import {List} from 'immutable';
import MultipleSelectField from 'components/pure/utils/select-field/MultipleSelectField';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

/**
 * A `EnumSearchField` renders a material-ui `RadioButtonGroup`, each of whose
 * radio buttons represents an enum value
 */
@componentHoc
class EnumSearchField extends React.Component {
  constructor(props) {
    super(props);

    this.removeEnumFromSelectedEnumOptions = this.removeEnumFromSelectedEnumOptions.bind(this);
    this.onToggleEnumSearchField = this.onToggleEnumSearchField.bind(this);
    this.handleSearchFieldSearchTermChange = this.handleSearchFieldSearchTermChange.bind(this);
    this.addEnumToSelectedEnumOptions = this.addEnumToSelectedEnumOptions.bind(this);

    this.state = {
      toggled: false,
      currentSearchTerm: '',
      selectedEnumOptions: List(),
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (typeof nextProps.toggled !== 'undefined') {
      this.setState({
        toggled: nextProps.toggled,
      });
    }
  }

  /* Return a string that can be displayed in-place of the enum value label
   * @param {immutable.Map} opt [description]
   * @return {[type]} [description]
   */
  enumDisplayValue(opt) {
    const count = opt.get('count');
    const display = opt.get('display');
    return count > 0 ? `${display} (${count})` : `${display}`;
  }

  /**
   * Callback triggered when the user toggle / un-toggle the search widget
   * @param {object} event An event object
   * @param {boolean} toggled A boolean indicating whether the search widget is toggled or not
   */
  onToggleEnumSearchField(event, toggled) {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.enumWidgetType,
      toggled,
      this.props.searchField
    );

    setTimeout(() => {
      SearchFieldsActions.updateEnumSearchExpression(this.props.searchField, toggled);
    }, ConstValues.delayDurationForSearchWidgetToggles);

    if (toggled) {
      this.props.updateLastToggledOnSearchWidget(this.props.searchField.name);
    } else {
      this.clearSelectedEnumOptions();
    }
  }

  /**
   * Clears the currently list of currently selected enum options - selectedEnumOptions.
   */
  clearSelectedEnumOptions() {
    this.setState({selectedEnumOptions: List()});
  }

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls to the widget if it is the most recent Search Widgets to be toggled on.
   */
  componentDidUpdate() {
    if (this.props.shouldFocus) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // Reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  /**
   * Returns a list of selected enums formatted as map with id, value, and label.
   * @return {list} A list of maps containing id, value, and formatted label for the currently selected enum options.
   */
  currentEnums() {
    return this.state.selectedEnumOptions
      .sort(this.sortSearchFieldOptionsCriteria)
      .map(opt => {
        return {id: opt.display, value: opt, label: this.enumDisplayValue(opt)};
      })
      .toArray();
  }

  /*
   * Updates the dropdown search term as the user types.
   */
  handleSearchFieldSearchTermChange(searchTerm) {
    this.setState({currentSearchTerm: searchTerm});
  }

  /**
   * Call handle selected options change with the new list of selected Field option objects create by
   * removing the provided enum option.
   * @param enumOptionRemoved {FieldOption} The field option object to remove from the selected list.
   */
  removeEnumFromSelectedEnumOptions(enumOptionRemoved) {
    if (enumOptionRemoved) {
      let newSelectedEnumOptions = this.state.selectedEnumOptions;
      const index = newSelectedEnumOptions.indexOf(enumOptionRemoved.value);
      if (index > -1) {
        newSelectedEnumOptions = newSelectedEnumOptions.delete(index);
        this.handleSelectedEnumOptionsChange(newSelectedEnumOptions);
      }
    }
  }

  /**
   * Call handle selected options change with the new list of selected Field option objects
   * that includes the provided enum field option.
   * @param enumFieldOption {FieldOption} The enum field option object to add to the selected list.
   */
  addEnumToSelectedEnumOptions(enumFieldOption) {
    if (enumFieldOption) {
      this.handleSelectedEnumOptionsChange(this.state.selectedEnumOptions.push(enumFieldOption));
    }
  }

  /**
   * Handles a change to the selected enum options by updating state with the new options
   * and then formatting the options to update the search criteria.
   * @param {List} newSelectedEnumOptions The new list of selected Field option objects.
   */
  handleSelectedEnumOptionsChange(newSelectedEnumOptions) {
    const selectedList = newSelectedEnumOptions
      .map(opt => {
        return opt.display;
      })
      .toArray();
    this.setState({selectedEnumOptions: newSelectedEnumOptions});
    this.props.onEnumChange(this.props.searchField, selectedList);
  }

  /*
   * Returns a list of field options formatted for multiselect.
   * return {list} A list of maps containing value (field option obj) and formatted label.
   */
  getSearchFieldOptions() {
    const options = this.props.searchField.options;
    const selected = this.state.selectedEnumOptions;
    return options
      .sort(this.sortSearchFieldOptionsCriteria)
      .filterNot(opt => {
        return selected.findLast(item => item.get('display') === opt.get('display'));
      })
      .map(opt => {
        return {value: opt, label: this.enumDisplayValue(opt)};
      })
      .toArray();
  }

  /**
   * Returns a number negative, positive, or 0 for use with array.sort to determine the sort order
   * of the enum search field options based on the count.
   * This sort criteria will sort larger counts first.
   * @param optionA {map} An enum search field option map. Map must contain count.
   * @param optionB {map} An enum search field option map. Map must contain count.
   * @return {float} Indicates the sorting order of the two supplied options based on count
   */
  sortSearchFieldOptionsCriteria(optionA, optionB) {
    return optionB.count - optionA.count;
  }

  render() {
    const {searchField} = this.props;
    const {currentSearchTerm, toggled} = this.state;
    const classes = cn('search-widget', getSearchFieldClass(searchField.name));
    return (
      <div className={classes}>
        <div>
          <Toggle
            label={searchField.name}
            defaultToggled={false}
            onToggle={this.onToggleEnumSearchField}
            toggled={toggled}
          />
          <Divider style={{marginTop: '5px'}} />
          {toggled && (
            <MultipleSelectField
              removeItem={this.removeEnumFromSelectedEnumOptions}
              selectField={{
                name: searchField.name,
                className: 'search-sidebar-select-field',
                placeholder: 'Type to search',
                search: currentSearchTerm,
                onSearchChange: this.handleSearchFieldSearchTermChange,
                noResultsMessage: 'No options matching your search.',
                options: this.getSearchFieldOptions(),
                onChange: this.addEnumToSelectedEnumOptions,
              }}
              selectedList={{
                containerClassname: 'enum-list-container',
                listClassname: 'enum-list',
                spanClassname: 'enum-span',
                items: this.currentEnums(),
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default EnumSearchField;
